import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { hasPermission } from "../../../utils/permissionUtils";
import {
    Box, Typography, Alert, Button, CircularProgress, Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import ListRelationships from "./ListRelationships";
import AddEditRelationshipModal from "./AddEditRelationshipModal";

export default function KnowledgeGraphPage() {
    const { chatbotId } = useParams();

    // Permissions
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // UI State
    const [showAddModal, setShowAddModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        document.title = "Knowledge Graph | KlonIT Web Application";
        checkPermissions();
    }, []);

    const checkPermissions = async () => {
        try {
            const viewPerm = await hasPermission("knowledge-graph", "view");
            const modifyPerm = await hasPermission("knowledge-graph", "add|edit");
            const deletePerm = await hasPermission("knowledge-graph", "delete");
            setCanView(viewPerm);
            setCanModify(modifyPerm);
            setCanDelete(deletePerm);
        } catch (e) {
            console.error("Error checking permissions:", e);
        } finally {
            setCheckingPermissions(false);
        }
    };

    const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

    const handleEdit = (rel) => {
        setEditData(rel);
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditData(null);
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canView) {
        return (
            <Alert severity="warning" sx={{ m: 2 }}>
                You do not have permission to view the Knowledge Graph.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        Knowledge Graph
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage relationships between entities. Connections are automatically extracted when content is added or edited.
                    </Typography>
                </Box>
                {canModify && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddModal(true)}
                    >
                        Add Relationship
                    </Button>
                )}
            </Box>

            {/* Relationships Table */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <ListRelationships
                    refresh={refreshTrigger}
                    onEdit={handleEdit}
                    canModify={canModify}
                    canDelete={canDelete}
                />
            </Paper>

            {/* Modal */}
            <AddEditRelationshipModal
                open={showAddModal}
                onClose={handleCloseModal}
                onSave={triggerRefresh}
                editData={editData}
            />
        </Box>
    );
}
